<script setup lang="ts">
import { type ModelRef } from 'vue';
import type { Emits, Props } from './types';
import type { Folder } from './folders/types';
import type { Group } from './groups/types';
import Folders from './folders/folders.vue';
import Groups from './groups/groups.vue';
import { folderDefault, genFlat, genRootFolder } from './folders/utils';
import { genGroupAll, genGroupPlaceholder, groupNone } from './groups/utils';

const props = withDefaults(defineProps<Props>(), {
	on: undefined,
	canSelectAll: false,
	autoselect: 'first',
	addChanger: true,
	showFolders: true,
	showGroups: true,
	addIcon: true,
});

const modelFolderId = defineModel<Props['folderId']>('folderId', { required: true });
const modelFolder = defineModel<Props['folder']>('folder');

const modelGroupId = defineModel<Props['groupId']>('groupId', { required: true });
const modelGroup = defineModel<Props['group']>('group', { default: groupNone }) as ModelRef<NonNullable<Props['group']>>;

const emit = defineEmits<Emits>();

/**
 * Выбранная папка
 *
 * Спекулятивное предсказание выбранной папки
 *
 * Если папка не определена, она ставится в `undefined`, чтобы при последующем определении не фиксировались события смены папки, см. `groups.vue`
 */
const foldersFlat = genFlat(props.folders ?? { root: [folderDefault] }, props.canSelectAllGroups);
modelFolder.value = foldersFlat.get(modelFolderId.value);

/**
 * Спекулятивное предсказание выбранной группы, чтобы не было лишней перерисовки
 *
 * Точная проверка происходит в `groups.vue`
 */
let groupInitial = props.groups?.find((group) => group.id === modelGroupId.value);
if (!modelGroupId.value) groupInitial = props.groups?.[0];
if (props.canSelectAllGroups && !groupInitial) {
	groupInitial = genGroupAll();
}
if (props.canSelectAllGroups && !groupInitial) {
	groupInitial = genGroupAll();
}
if (props.autoselect === 'placeholder' || !groupInitial) {
	groupInitial = genGroupPlaceholder();
}
groupInitial ??= groupNone;

modelGroup.value = groupInitial;

const onAddGroup = (group: Group) => {
	// если загрузка групп производится по api, то добавлять новые группы в `props.groups` не нужно
	let groups: Group[] | undefined;

	if (props.groups && group) {
		groups = [...props.groups, group];
	}

	emit('update:groups', groups);
};
</script>

<template>
	<div class="top-groupSelector">
		<Folders
			v-if="showFolders"
			v-model:folderId="modelFolderId"
			v-model:folder="modelFolder"
			:projectId
			:folders
			:canSelectAll="canSelectAllGroups"
			:addChanger
			:addIcon
			:client
		/>

		<Groups
			v-if="showGroups"
			v-model:groupId="modelGroupId"
			v-model:group="modelGroup"
			:projectId
			:folder="modelFolder"
			:groups
			:on
			:canAdd="canAddGroup"
			:canSelectAll="canSelectAllGroups"
			:autoselect
			:addChanger
			:showPath="showFolders && !!folders?.[0]"
			:addIcon
			:client
			@addGroup="onAddGroup"
		/>
	</div>
</template>

<style>
.top-groupSelector {
	display: inline-flex;
	gap: var(--top-padding-1);
}
</style>
